import { useEffect, useState } from "react"
import type { AdBanner } from "@ezbatna/core/domain/models"
import { AlertCircle } from "lucide-react"
import { getContainer } from "../lib/container"
import { Alert, Button, Card, CardBody, CardFooter, CardHeader, Input, Spinner, useToast } from "../components/ui"

function toInputDate(date: Date | null): string {
  if (!date) return ""
  const tz = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - tz).toISOString().slice(0, 10)
}

function fromInputDate(value: string): Date | null {
  return value ? new Date(`${value}T00:00:00`) : null
}

export function AdBannerPage() {
  const [banner, setBanner] = useState<AdBanner | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let cancelled = false
    getContainer()
      .settingsRepository.fetchAdBanner()
      .then((b) => {
        if (!cancelled) setBanner(b)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = async () => {
    if (!banner) return
    setSaving(true)
    try {
      await getContainer().settingsRepository.updateAdBanner(banner)
      toast.show("تم حفظ إعدادات شريط الإعلانات", "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">شريط الإعلانات</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">التحكم بشريط الإعلان الترويجي الظاهر داخل التطبيق</p>
      </div>

      <Alert variant="warning">
        <span className="inline-flex items-center gap-2xs">
          <AlertCircle className="size-4" />
          لا يوجد بث لحظي — يطبّق تطبيق iOS التغييرات عند التشغيل البارد التالي فقط، وليس فوراً على الجلسات المفتوحة حالياً.
        </span>
      </Alert>

      <Card>
        {loading || !banner ? (
          <CardBody>
            <div className="flex items-center justify-center py-2xl">
              <Spinner />
            </div>
          </CardBody>
        ) : (
          <>
            <CardHeader className="flex items-center justify-between gap-md">
              <div>
                <h2 className="text-title font-bold text-text-primary">إعدادات الشريط</h2>
                <p className="mt-2xs text-body-sm text-text-secondary">فعّل الشريط وحدد صورته ومدته ووجهته</p>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-sm">
                <span className="text-label font-semibold text-text-primary">مفعّل</span>
                <input
                  type="checkbox"
                  checked={banner.enabled}
                  onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                  className="size-5 accent-accent"
                />
              </label>
            </CardHeader>
            <CardBody className="flex flex-col gap-lg">
              <Input
                label="رابط الصورة"
                value={banner.imageUrl ?? ""}
                onChange={(e) => setBanner({ ...banner, imageUrl: e.target.value || null })}
                placeholder="https://…"
              />
              <Input
                label="رابط الوجهة عند الضغط"
                value={banner.pointToUrl ?? ""}
                onChange={(e) => setBanner({ ...banner, pointToUrl: e.target.value || null })}
                placeholder="https://…"
              />
              <div className="grid gap-lg sm:grid-cols-2">
                <Input
                  type="date"
                  label="تاريخ البداية"
                  value={toInputDate(banner.startDate)}
                  onChange={(e) => setBanner({ ...banner, startDate: fromInputDate(e.target.value) })}
                />
                <Input
                  type="date"
                  label="تاريخ النهاية"
                  value={toInputDate(banner.endDate)}
                  onChange={(e) => setBanner({ ...banner, endDate: fromInputDate(e.target.value) })}
                />
              </div>

              {banner.imageUrl && (
                <div>
                  <p className="mb-xs text-label font-semibold text-text-primary">معاينة</p>
                  <div className="overflow-hidden rounded-box border border-border-subtle">
                    <img src={banner.imageUrl} alt="معاينة شريط الإعلانات" className="max-h-48 w-full object-cover" />
                  </div>
                </div>
              )}
            </CardBody>
            <CardFooter className="flex justify-end">
              <Button onClick={handleSave} loading={saving}>
                حفظ
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  )
}
